import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Button,
  ModalFooter,
  VStack,
  Radio,
  useToast,
  Textarea,
  FormControl,
  FormLabel,
  Box,
  Image,
} from '@chakra-ui/react';
import { t, Trans } from '@lingui/macro';
import { Api } from 'api';
import { FormControlFormRadioGroup, useGetUploadedClientFile, useUploadClientFile } from 'core';
import { hideLoading, showLoading } from 'features';
import { useFormik } from 'formik';
import React, { useRef } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store';
import { CustomerExpertiseFileType, CustomerQueryCacheKey, ExpertiseBodyDamagePartType } from 'types';

export interface AirbagCheckResultsProps extends Omit<ModalProps, 'children'> {
  customerId: number;
}

export const AirbagCheckResults: React.FC<AirbagCheckResultsProps> = ({ customerId, ...modalProps }) => {
  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch existing airbag result
  const { data: airbagCheckResult, refetch: refetchResult } = useQuery(
    [CustomerQueryCacheKey.GetAirbagCheckResult, customerId],
    async () => {
      const response = await Api.Customer.getAirbagCheckResult(customerId);
      return response.data;
    },
    {
      onError: () => {
        toast({
          title: t`Airbag kontrol sonuçları çekilirken hata oluştu`,
          status: 'error',
        });
      },
      refetchOnWindowFocus: false,
      cacheTime: 0,
    },
  );

  // Fetch existing opinion note
  const { data: opinionNote, refetch: refetchNote } = useQuery(
    ['GetAirbagOpinionNote', customerId],
    async () => {
      const response = await Api.Expertise.getOpinionNote(customerId, ExpertiseBodyDamagePartType.AirbagControl);
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  // Fetch existing file
  const { data: uploadedFile, refetch: refetchFile } = useGetUploadedClientFile(
    customerId,
    CustomerExpertiseFileType.AirbagCheckResult,
    {
      onError: (err: any) => {
        console.error(err);
      },
    },
  );

  const uploadFileMutation = useUploadClientFile(customerId, CustomerExpertiseFileType.AirbagCheckResult, {
    onMutate: () => {
      dispatch(showLoading());
    },
    onSettled: () => {
      dispatch(hideLoading());
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      refetchFile();
    },
    onError: (error: any) => {
      console.error(error);
      toast({
        status: 'error',
        title: t`Dosya yüklenirken hata oluştu`,
        isClosable: true,
      });
    },
  });

  const saveMutation = useMutation(
    async (values: { resultType: string; note: string }) => {
      dispatch(showLoading());
      try {
        // Save result
        await Api.Customer.saveAirbagCheckResult(customerId, +values.resultType);
        // Save note
        await Api.Expertise.saveOpinionNote(customerId, ExpertiseBodyDamagePartType.AirbagControl, values.note);
      } finally {
        dispatch(hideLoading());
      }
    },
    {
      onSuccess: () => {
        toast({
          title: t`Airbag Kontrol Sonucu Güncellendi`,
          status: 'success',
        });
        modalProps.onClose();
        refetchResult();
        refetchNote();
      },
      onError: (error: any) => {
        console.error(error);
        toast({
          title: t`Kaydedilirken hata oluştu`,
          status: 'error',
        });
      },
    },
  );

  const { values, setFieldValue, handleSubmit, isSubmitting } = useFormik({
    enableReinitialize: true,
    initialValues: {
      resultType: airbagCheckResult ? airbagCheckResult.resultType.toString() : '',
      note: opinionNote ? opinionNote.note : '',
    },
    onSubmit: (formValues) => {
      saveMutation.mutate(formValues);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      uploadFileMutation.mutate(e.target.files[0]);
    }
  };

  return (
    <>
      <input type="file" accept="image/*" hidden ref={fileInputRef} onChange={handleFileChange} />
      <Modal {...modalProps} isCentered size="lg">
        <ModalOverlay />
        <form noValidate onSubmit={handleSubmit} style={{ width: '100%' }}>
          <ModalContent>
            <ModalHeader>
              <Trans>Airbag Kontrolü</Trans>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={6} align="stretch">
                <FormControlFormRadioGroup
                  name="resultType"
                  label={t`Kontrol Sonucu`}
                  onChange={(value) => setFieldValue('resultType', value)}
                  value={values.resultType}
                >
                  <VStack spacing={3} align="start" mt={2}>
                    <Radio value="10">
                      <Trans>Sorunsuz</Trans>
                    </Radio>
                    <Radio value="20">
                      <Trans>Acil Kontrol</Trans>
                    </Radio>
                    <Radio value="30">
                      <Trans>Tamirli</Trans>
                    </Radio>
                    <Radio value="40">
                      <Trans>İşlem Yapılmadı</Trans>
                    </Radio>
                  </VStack>
                </FormControlFormRadioGroup>

                <FormControl>
                  <FormLabel>
                    <Trans>Eksper Notu</Trans>
                  </FormLabel>
                  <Textarea
                    placeholder="..."
                    value={values.note}
                    onChange={(e) => setFieldValue('note', e.target.value)}
                  />
                </FormControl>

                <Box>
                  <FormLabel>
                    <Trans>Fotoğraf</Trans>
                  </FormLabel>
                  {uploadedFile ? (
                    <Box position="relative" borderRadius="md" overflow="hidden" border="1px" borderColor="gray.200">
                      <Image src={uploadedFile.url} alt="Airbag Check" objectFit="cover" w="100%" h="200px" />
                      <Button
                        size="sm"
                        position="absolute"
                        top={2}
                        right={2}
                        colorScheme="blue"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Trans>Değiştir</Trans>
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      w="100%"
                      variant="outline"
                      leftIcon={
                        <Box as="span" fontSize="xl">
                          📷
                        </Box>
                      }
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Trans>Fotoğraf Ekle</Trans>
                    </Button>
                  )}
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={modalProps.onClose}>
                <Trans>Vazgeç</Trans>
              </Button>
              <Button
                colorScheme="teal"
                type="submit"
                isLoading={isSubmitting || saveMutation.isLoading}
                isDisabled={!values.resultType}
              >
                <Trans>Kaydet</Trans>
              </Button>
            </ModalFooter>
          </ModalContent>
        </form>
      </Modal>
    </>
  );
};
